import React, { useState, useEffect } from 'react';
import { FaTimes, FaBuilding, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PlantafelService, { ProjectTimelineItem } from '../services/PlantafelService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import { pdebugError } from '../utils/plantafelDebug';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface CreateProjectModalProps {
  isOpen: boolean;
  defaultStartDate?: string;
  defaultEndDate?: string;
  onClose: () => void;
  onCreated: (project: ProjectTimelineItem) => void;
}

const PROJECT_STATUSES = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'OFFER_CREATED', label: 'Angebot erstellt' },
  { value: 'OFFER_SENT', label: 'Angebot gesendet' },
  { value: 'ORDER_CONFIRMED', label: 'Auftrag bestätigt' },
  { value: 'IN_EXECUTION', label: 'In Ausführung' },
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'WARRANTY', label: 'Gewährleistung' },
];

export default function CreateProjectModal({
  isOpen,
  defaultStartDate,
  defaultEndDate,
  onClose,
  onCreated,
}: CreateProjectModalProps): React.JSX.Element | null {
  const [partners, setPartners] = useState<BusinessPartner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);

  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [plannedStart, setPlannedStart] = useState(defaultStartDate || '');
  const [plannedEnd, setPlannedEnd] = useState(defaultEndDate || '');
  const [status, setStatus] = useState('IN_EXECUTION');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoadingPartners(true);
      BusinessPartnerService.list({ kind: 'CUSTOMER', active: true })
        .then((data) => {
          setPartners(data);
        })
        .catch((err) => {
          pdebugError('Failed to load customers:', err);
          toast.error('Fehler beim Laden der Kunden');
        })
        .finally(() => {
          setLoadingPartners(false);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (defaultStartDate) setPlannedStart(defaultStartDate);
    if (defaultEndDate) setPlannedEnd(defaultEndDate);
  }, [defaultStartDate, defaultEndDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!partnerId) {
      setError('Bitte Kunde auswählen');
      return;
    }

    if (!name.trim()) {
      setError('Bitte Projektname eingeben');
      return;
    }

    setIsSubmitting(true);
    try {
      const project = await PlantafelService.createProject({
        partner_id: partnerId,
        name: name.trim(),
        planned_start: plannedStart || undefined,
        planned_end: plannedEnd || undefined,
        status,
      });

      toast.success('Projekt erstellt');
      onCreated(project);
      handleClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || 'Fehler beim Erstellen';
      setError(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPartnerId(null);
    setName('');
    setPlannedStart(defaultStartDate || '');
    setPlannedEnd(defaultEndDate || '');
    setStatus('IN_EXECUTION');
    setError(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getPartnerDisplayName = (partner: BusinessPartner): string => {
    if (partner.is_company && partner.company_name) {
      return partner.company_name;
    }
    const parts = [partner.first_name, partner.last_name].filter(Boolean);
    return parts.join(' ') || `Partner #${partner.id}`;
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          boxShadow: shadows.elevated,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: colors.navy,
            }}
          >
            Neues Projekt
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '8px',
                }}
              >
                <FaBuilding size={14} />
                Kunde *
              </label>
              <select
                value={partnerId || ''}
                onChange={(e) => setPartnerId(e.target.value ? parseInt(e.target.value, 10) : null)}
                disabled={loadingPartners}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  background: colors.bgCard,
                  cursor: loadingPartners ? 'wait' : 'pointer',
                }}
              >
                <option value="">
                  {loadingPartners ? 'Lade Kunden...' : 'Kunde auswählen...'}
                </option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {getPartnerDisplayName(partner)}
                    {partner.city && ` (${partner.city})`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '8px',
                }}
              >
                Projektname *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. Dachsanierung Hauptstraße 5"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: colors.navy,
                    marginBottom: '8px',
                  }}
                >
                  <FaCalendarAlt size={12} />
                  Gepl. Start
                </label>
                <input
                  type="date"
                  value={plannedStart}
                  onChange={(e) => setPlannedStart(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: colors.navy,
                    marginBottom: '8px',
                  }}
                >
                  <FaCalendarAlt size={12} />
                  Gepl. Ende
                </label>
                <input
                  type="date"
                  value={plannedEnd}
                  onChange={(e) => setPlannedEnd(e.target.value)}
                  min={plannedStart || undefined}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '8px',
                }}
              >
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  background: colors.bgCard,
                  cursor: 'pointer',
                }}
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                style={{
                  padding: '10px 12px',
                  background: colors.dangerLight,
                  border: `1px solid ${colors.danger}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: colors.danger,
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 20px',
              borderTop: `1px solid ${colors.border}`,
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                ...ps.buttonSecondary,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !partnerId || !name.trim()}
              style={{
                ...(isSubmitting || !partnerId || !name.trim() ? ps.buttonDisabled : ps.buttonPrimary),
              }}
            >
              {isSubmitting ? 'Wird erstellt...' : 'Projekt erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
